import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Grid, List, ChevronLeft, ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useFavorites } from '@/hooks/useFavorites';
import { QuoteCard } from '@/components/quote/QuoteCard';
import { QuoteSkeleton } from '@/components/ui/Skeleton';
import { SearchFilters } from '@/components/search/SearchFilters';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

export function Browse() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const params = { page: String(page), limit: '12', ...filters };
  const { data, isLoading } = useQuery<{ success: boolean; data: any[]; total: number; totalPages: number }>({
    queryKey: ['quotes', params],
    queryFn: () => api.quotes.getAll(params),
  });
  const { data: favData } = useFavorites();

  const quotes = data?.data || [];
  const totalPages = data?.totalPages || 1;
  const favorites = new Set((favData?.data || []).map((f: any) => f.quote_id || f.id));

  const handleUpdateFilter = (key: string, value: string) => {
    setFilters((prev) => {
      if (!value) {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: value };
    });
    setPage(1);
  };

  return (
    <motion.div initial="initial" animate="animate" className="max-w-6xl mx-auto px-4 py-12">
      <motion.div variants={fadeUp}>
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-bold mb-2">Browse Quotes</h1>
            <p className="text-surface-500">{data?.total || 0} quotes available</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-xl transition-colors ${viewMode === 'grid' ? 'bg-primary-50 text-primary-500' : 'text-surface-400 hover:bg-surface-100'}`}
            >
              <Grid size={20} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-xl transition-colors ${viewMode === 'list' ? 'bg-primary-50 text-primary-500' : 'text-surface-400 hover:bg-surface-100'}`}
            >
              <List size={20} />
            </button>
          </div>
        </div>

        <SearchFilters filters={filters} onUpdateFilter={handleUpdateFilter} />
      </motion.div>

      <div className="mt-8">
        {isLoading ? (
          <div className={viewMode === 'grid' ? 'grid md:grid-cols-2 gap-4' : 'flex flex-col gap-4'}>
            {Array.from({ length: 6 }).map((_, i) => (
              <QuoteSkeleton key={i} />
            ))}
          </div>
        ) : quotes.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-xl text-surface-500">No quotes found</p>
          </div>
        ) : (
          <div className={viewMode === 'grid' ? 'grid md:grid-cols-2 gap-4' : 'flex flex-col gap-4'}>
            {quotes.map((quote: any, index: number) => (
              <motion.div
                key={quote.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link to={`/quote/${quote.id}`}>
                  <QuoteCard
                    quote={quote}
                    isFavorited={favorites.has(quote.id)}
                  />
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-12">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn-secondary disabled:opacity-40"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="text-sm text-surface-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="btn-secondary disabled:opacity-40"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}
    </motion.div>
  );
}
